import { AnalyticsBrowser } from '@segment/analytics-next'
import { installAdDestinationGuard, SEGMENT_CDN_URL, SEGMENT_INIT_OPTIONS } from '@quickmd-llc/frontend-utils/segment'
import { type ProviderPatientSourceEventName } from '@quickmd-llc/frontend-utils/events'
import { EventLocations } from 'lib/segment/constants'
import { PROVIDER_EVENT_NAMES, type Events as ProviderEvents, tracking as providerTracking } from 'lib/segment/tracking'
import { buildVisitEventProperties } from 'lib/segment/utils'
import moment from 'moment'
import { type Patient, type Visit } from 'types/types'
import env from '../../../env.json'

// A second Segment client, on the *patient* source rather than the provider one,
// so patient visit events land where their downstream consumers expect them.
// That also means it inherits the patient source's device-mode destinations —
// inside a clinician's live visit room. Hence the same guards as
// lib/segment/tracking.ts. See QMD-4332.
const segmentClient = AnalyticsBrowser.load(
  { writeKey: env.patientSegmentWriteKey, cdnURL: SEGMENT_CDN_URL },
  SEGMENT_INIT_OPTIONS,
)

installAdDestinationGuard(segmentClient)

/**
 * Event names provider sends through the patient Segment source (QMD-4636/QMD-4638). 'Visit
 * Queued' had no call site anywhere and is not in this shared list.
 */
export type PatientVisitEvent = ProviderPatientSourceEventName

type TrackPatientVisitEventParams = {
  patient: Patient | undefined
  providerEmail: string | undefined
  visit: Partial<Visit>
}

export function trackPatientVisitEvent(
  event: PatientVisitEvent,
  { patient, providerEmail, visit }: TrackPatientVisitEventParams,
) {
  segmentClient.track(event, {
    serviceTypeName: visit.ServiceTypeName ?? '',
    visitTypeName: visit.VisitTypeName ?? '',
    method: visit.Method ?? '',
    visitId: visit.VisitId ?? '',
    visitDateTime: visit.DateTime ?? '',
    timestamp: moment().toISOString(),
    patientMrn: patient?.MedicalRecordNumber ?? '',
    patientId: patient?.PatientId ?? '',
    ProviderEmail: providerEmail ?? '',
  })
}

type TrackProviderVisitEventParams = {
  visit: Partial<Visit>
  patient: Patient
}

export function trackProviderVisitEvent(event: ProviderEvents, { patient, visit }: TrackProviderVisitEventParams) {
  const properties = {
    Method: visit.Method ?? '',
    PatientId: patient.PatientId ?? '',
    PatientMrn: patient.MedicalRecordNumber ?? '',
    ServiceTypeName: visit.ServiceTypeName ?? '',
    VisitDateTime: visit.DateTime ?? '',
    VisitId: visit.VisitId ?? '',
    VisitTypeName: visit.VisitTypeName ?? '',
  }

  providerTracking.trackEvent(event, properties)
}

export function trackSaveVisitNote(inProgressVisit: any) {
  const location = EventLocations.inProgressVisit
  const visit = {
    ...inProgressVisit,
    PatientId: inProgressVisit.Patient.PatientId,
  }

  const properties = buildVisitEventProperties({ location, visit })

  if (!properties) {
    return
  }

  // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
  providerTracking.trackEvent(PROVIDER_EVENT_NAMES.SAVE_VISIT_NOTE, properties)
}
